import { useEffect, useMemo, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconView } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface PenaltyRow {
  id: number;
  employer_id: number;
  employer: { id: number; name: string };
  reason_code: string;
  amount: number;
  status: 'assessed' | 'waiver_requested' | 'waived' | 'paid';
  due_date: string;
}

interface EmployerOption {
  id: number;
  name: string;
}

const STATUS_LABELS: Record<string, string> = {
  assessed: 'Assessed',
  waiver_requested: 'Waiver Requested',
  waived: 'Waived',
  paid: 'Paid',
};

const STATUS_BADGE: Record<string, string> = {
  assessed: 'badge-scheduled',
  waiver_requested: 'badge-neutral',
  waived: 'badge-active',
  paid: 'badge-active',
};

const emptyForm = { employer_id: '', reason_code: '', supporting_reference: '', basis_amount: '' };

function formatDueDate(value: string): string {
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function Penalties() {
  const { can } = useAuth();
  const { settings, lists } = useConfig();
  const [status, setStatus] = useState('');

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [employers, setEmployers] = useState<EmployerOption[]>([]);

  const canCreate = can('penalties.create');

  const filters = useMemo(() => ({ status: status || undefined }), [status]);
  const { data, loading, error, setPage, reload } = usePaginatedList<PenaltyRow>('/penalties', filters);

  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  useEffect(() => {
    if (!canCreate) return;
    api
      .get<{ data: EmployerOption[] }>('/employers', { params: { per_page: 100 } })
      .then((res) => setEmployers(res.data.data))
      .catch(() => setEmployers([]));
  }, [canCreate]);

  const openAdd = () => {
    setForm(emptyForm);
    setErrors({});
    setAdding(true);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/penalties', {
        employer_id: form.employer_id,
        reason_code: form.reason_code,
        supporting_reference: form.supporting_reference,
        basis_amount: Number(form.basis_amount),
      });
      setAdding(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Penalties &amp; Surcharges</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Raise Penalty
          </button>
        )}
      </header>

      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Employer</th>
                <th>Reason</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((penalty) => (
                  <tr key={penalty.id}>
                    <td>
                      <strong>{penalty.employer.name}</strong>
                    </td>
                    <td className="muted">{labelFor(lists, 'penalty_reason_code', penalty.reason_code)}</td>
                    <td>{fmtMoney(penalty.amount)}</td>
                    <td className="muted">{formatDueDate(penalty.due_date)}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[penalty.status] ?? 'badge-neutral'}`}>
                        {STATUS_LABELS[penalty.status] ?? penalty.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/memberships/penalties/${penalty.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No penalties recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="Raise a manual penalty" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Employer *
              <select
                value={form.employer_id}
                onChange={(e) => setForm({ ...form, employer_id: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {employers.map((employer) => (
                  <option key={employer.id} value={employer.id}>
                    {employer.name}
                  </option>
                ))}
              </select>
              {errors.employer_id && <small className="error">{errors.employer_id[0]}</small>}
            </label>
            <label className="span-2">
              Reason *
              <select
                value={form.reason_code}
                onChange={(e) => setForm({ ...form, reason_code: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {listOptions(lists, 'penalty_reason_code').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.reason_code && <small className="error">{errors.reason_code[0]}</small>}
            </label>
            <label className="span-2">
              Supporting reference *
              <input
                value={form.supporting_reference}
                onChange={(e) => setForm({ ...form, supporting_reference: e.target.value })}
                required
              />
              {errors.supporting_reference && <small className="error">{errors.supporting_reference[0]}</small>}
            </label>
            <label className="span-2">
              Basis amount *
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.basis_amount}
                onChange={(e) => setForm({ ...form, basis_amount: e.target.value })}
                required
              />
              {errors.basis_amount && <small className="error">{errors.basis_amount[0]}</small>}
              <small className="muted">The penalty amount is computed from this using the configured rate.</small>
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

